// Client-side image compression.
//
// The dev API sits behind an nginx proxy with a ~1MB body limit, so phone
// photos (3-5MB HEIC/JPEG) return 413 on upload. We downscale + re-encode the
// image to JPEG in the browser before sending, keeping it well under the limit
// while preserving enough resolution for handwriting OCR.

const DEFAULTS = {
    maxDimension: 2400, // longest side in px (enough for legible handwriting)
    maxBytes: 900 * 1024, // stay safely under the 1MB nginx limit (multipart overhead)
    minDimension: 1000, // never shrink below this, or OCR accuracy degrades
    mimeType: 'image/jpeg',
};

const isHeic = (file) =>
    /image\/(heic|heif)/i.test(file.type) || /\.(heic|heif)$/i.test(file.name);

// HEIC/HEIF (iPhone photos) cannot be decoded by <canvas> in most browsers,
// and OpenAI's vision API rejects HEIC. Convert to JPEG first via heic2any
// (libheif compiled to wasm, works in Chrome too). Loaded lazily.
const convertHeic = async (file) => {
    try {
        const heic2any = (await import('heic2any')).default;
        const out = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.92 });
        const blob = Array.isArray(out) ? out[0] : out;
        return new File([blob], file.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' });
    } catch (e) {
        console.error('HEIC conversion failed:', e);
        return file; // fall back to original; caller may still try canvas decode
    }
};

const loadBitmap = async (file) => {
    // createImageBitmap decodes most formats (incl. HEIC on Safari) off the main thread.
    if (typeof createImageBitmap === 'function') {
        try {
            return await createImageBitmap(file);
        } catch (e) {
            // fall through to the <img> path
        }
    }
    return await new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
        img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('decode-failed')); };
        img.src = url;
    });
};

const drawScaled = (source, scale) => {
    const w = Math.max(1, Math.round((source.width || source.naturalWidth) * scale));
    const h = Math.max(1, Math.round((source.height || source.naturalHeight) * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff'; // flatten any transparency for JPEG
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(source, 0, 0, w, h);
    return canvas;
};

const toBlob = (canvas, mimeType, quality) =>
    new Promise((resolve) => canvas.toBlob((b) => resolve(b), mimeType, quality));

/**
 * Compress an image File to a JPEG File under `maxBytes`.
 * Falls back to the original file if the browser cannot decode it (e.g. HEIC on Chrome).
 */
export const compressImage = async (file, options = {}) => {
    const opts = { ...DEFAULTS, ...options };

    if (!file.type.startsWith('image/') && !/\.(jpe?g|png|webp|gif|bmp|tiff?|avif|heic|heif)$/i.test(file.name)) {
        return file;
    }

    // Convert HEIC/HEIF to JPEG before anything else.
    const input = isHeic(file) ? await convertHeic(file) : file;

    let source;
    try {
        source = await loadBitmap(input);
    } catch (e) {
        // Undecodable in this browser — send as-is.
        return input;
    }

    const naturalMax = Math.max(source.width || source.naturalWidth, source.height || source.naturalHeight);
    let dimension = Math.min(opts.maxDimension, naturalMax);

    // Reduce quality first, then dimension, until under the byte budget.
    for (let attempt = 0; attempt < 6; attempt++) {
        const canvas = drawScaled(source, dimension / naturalMax);
        for (const quality of [0.85, 0.75, 0.65, 0.55]) {
            const blob = await toBlob(canvas, opts.mimeType, quality);
            if (blob && (blob.size <= opts.maxBytes || (dimension <= opts.minDimension && quality === 0.55))) {
                const name = input.name.replace(/\.[^.]+$/, '') + '.jpg';
                if (typeof source.close === 'function') source.close();
                return new File([blob], name, { type: opts.mimeType, lastModified: Date.now() });
            }
        }
        if (dimension <= opts.minDimension) break;
        dimension = Math.max(opts.minDimension, Math.round(dimension * 0.8));
    }

    // Last resort: smallest render we produced.
    const canvas = drawScaled(source, Math.min(1, opts.minDimension / naturalMax));
    const blob = await toBlob(canvas, opts.mimeType, 0.55);
    if (typeof source.close === 'function') source.close();
    if (!blob) return file;
    const name = file.name.replace(/\.[^.]+$/, '') + '.jpg';
    return new File([blob], name, { type: opts.mimeType, lastModified: Date.now() });
};
